'use client';
import Link from 'next/link';
import { Logo } from '@components/elements/Logo';
import { Bars3Icon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useScreenSize } from '@hooks/useScreenSize';
import clsx from 'clsx';
import SessionAvatar from './components/SessionAvatar';
import FullScreenSearch from '@components/elements/SearchBar/FullScreenSearch';
import SearchBar from '@components/elements/SearchBar/SearchBar';
import { useFullScreenSearch } from '@contexts/fullscreensearch';
import { trackEvent } from '../../../lib/analytics/posthog';
import { EVENTS } from '../../../lib/analytics/events';

/**
 * Barra superior, en tres versiones (ver DESIGN.md):
 * - `landing`: transparente sobre la portada, con «Mapa · Categorías»; al bajar se vuelve blanca y
 *   suma el buscador.
 * - `map`: barra de vidrio que flota sobre el mapa de /negocios.
 * - `bar`: blanca con línea inferior; muestra la ruta (`breadcrumb`) o, si no hay, el buscador.
 */
export default function Header({
  handleToggle,
  scroll,
  variant = 'bar',
  showSearchBar = true,
  breadcrumb = null,
  hideSearch = false
}) {
  const { isDesktop } = useScreenSize();
  const { showSearchFullScreen, setShowSearchFullScreen } = useFullScreenSearch();

  if (showSearchFullScreen) return <FullScreenSearch setShow={setShowSearchFullScreen} />;

  const isLanding = variant === 'landing';
  const solid = isLanding && scroll;
  const searchAvailable = !hideSearch && !breadcrumb && (isLanding ? showSearchBar : true);
  const hasSearch = searchAvailable && isDesktop;
  const showPhoneSearch = searchAvailable && !isDesktop;

  return (
    <nav
      aria-label="Navegación principal"
      className={clsx('topbar', `topbar--${variant}`, { 'is-solid': solid })}
    >
      <div className="topbar__inner">
        <Link href="/" className="topbar__logo" aria-label="Ágora, inicio">
          <Logo type="horizontal" width="100%" height="100%" color="#0b1324" />
        </Link>

        {isLanding && (
          <div className="topbar__nav">
            <Link href="/negocios" className="topbar__nav-link is-current">
              Mapa
            </Link>
            <Link href="/directorio" className="topbar__nav-link">
              Categorías
            </Link>
          </div>
        )}

        {breadcrumb?.length > 0 && (
          <nav className="topbar__crumbs" aria-label="Ruta">
            {breadcrumb.map((item, i) => (
              <span key={item.href || item.label}>
                {i > 0 && <span aria-hidden="true"> / </span>}
                {item.href ? <Link href={item.href}>{item.label}</Link> : <span>{item.label}</span>}
              </span>
            ))}
          </nav>
        )}

        {hasSearch ? (
          <div className="topbar__search animate-navbar-fade-in">
            <SearchBar />
          </div>
        ) : (
          <div className="topbar__spacer" />
        )}

        {showPhoneSearch && (
          <button
            type="button"
            aria-label="Buscar"
            className="topbar__icon"
            onClick={() => {
              trackEvent(EVENTS.NAV_HEADER_SEARCH_OPENED, { device: 'mobile' });
              setShowSearchFullScreen(true);
            }}
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
          </button>
        )}

        <div className="topbar__actions">
          <SessionAvatar glassLogin={isLanding && !solid} />
          {!isDesktop && (
            <button
              type="button"
              aria-label="Abrir menú"
              onClick={() => {
                trackEvent(EVENTS.NAV_MOBILE_MENU_OPENED);
                handleToggle();
              }}
              className="topbar__icon"
            >
              <Bars3Icon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </nav>
  );
}
